import { Line } from "@ant-design/plots";
import { Button, Card, Col, DatePicker, Row, Space, Spin, Table } from "antd";
import { LINE_WISE_TARGET_ACHIEVEMENT } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import ListTable from "common/ListTable";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { formatNumberWithCommas } from "utils/functions";

export default function TargetVsAchievement() {
  const [selectedDate, setSelectedDate] = useState(moment());
  const [data, setData] = useState({
    dataSource: [],
    barChart: [],
    loading: false,
  });
  const [hasSearched, setHasSearched] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Fix round 1: guard against setState after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ux2(A/presets): dateOverride lets the Today/Yesterday buttons fetch with
  // the just-picked date instead of the stale `selectedDate` closure value.
  const handleSearch = async (dateOverride) => {
    const searchDate = dateOverride || selectedDate;
    setData({ dataSource: [], barChart: [], loading: true });
    setHasSearched(true);
    setFetchError(null);
    try {
      const query = `${LINE_WISE_TARGET_ACHIEVEMENT}?start_date=${searchDate.format("YYYY-MM-DD")}`;
      const response = await getData(query);

      if (!mountedRef.current) return;

      const sortedData = response.data.data.data
        .map((item) => {
          const unique_line_chief = [...new Set(item.line_chief.map((chief) => chief?.line_chief))].join(", ");
          const unique_buyer = [...new Set(item.buyer.map((buyer) => buyer?.buyer_short_name))].join(", ");

          return {
            ...item,
            line_chief: unique_line_chief,
            buyer: unique_buyer,
          };
        })
        .sort((a, b) => {
          const numA = parseInt(a.line.match(/\d+/));
          const numB = parseInt(b.line.match(/\d+/));
          return numA - numB;
        });

      setData({
        dataSource: sortedData,
        barChart: sortedData.map((item) => ({
          line: item.line,
          value: formatNumberWithCommas(item.achivement_from_be_req_qty),
        })),
        loading: false,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      if (!mountedRef.current) return;
      setData({ dataSource: [], barChart: [], loading: false });
      setFetchError("Could not load this list.");
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleReset = () => {
    setSelectedDate(moment());
    setData({ dataSource: [], barChart: [], loading: false });
    setHasSearched(false);
  };

  // Round 4 (T2): restored. Round 3 removed this because line-wise-target-achievement
  // measured 18.68s warm (75.54s cold) for today's date on a real data day
  // (T1-timings/timings.md row 20). Task 1's set-based rewrite of
  // LineWiseTargetAchievementService now answers the same seeded default in
  // 0.19-0.49s in-process and 0.32-0.39s kernel-dispatch
  // (D:\cgl-erp\patches\ux-round4\golden\timings.md), comfortably inside R4-5's
  // 5s-warm/15s-cold bar, so the mount-time fetch is safe again.
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const config = {
    data: data.barChart,
    xField: "line",
    yField: "value",
    point: {
      shapeField: "square",
      sizeField: 4,
    },
    interaction: {
      tooltip: {
        marker: false,
      },
    },
    style: {
      lineWidth: 2,
    },
  };

  const columns = [
    {
      title: "Sewing Line",
      dataIndex: "line",
      key: "line",
    },
    {
      title: "Line Chief",
      dataIndex: "line_chief",
      key: "line_chief",
    },
    {
      title: "Buyer",
      dataIndex: "buyer",
      key: "buyer",
    },
    {
      title: "Required quantity for B/Even",
      dataIndex: "require_qty",
      key: "require_qty",
      align: "right",
      render: (text) => formatNumberWithCommas(text),
    },
    {
      title: "IE given Target",
      dataIndex: "ie_given_target",
      key: "ie_given_target",
      align: "right",
      render: (text) => parseInt(text).toLocaleString(undefined, { maximumFractionDigits: 0 }),
    },
    {
      title: "IE Target % of B/E Qty",
      dataIndex: "ie_target_be_qty",
      key: "ie_target_be_qty",
      align: "right",
      render: (text) => `${formatNumberWithCommas(text)}%`,
    },
    {
      title: "Actual Production",
      dataIndex: "actual_production",
      key: "actual_production",
      align: "right",
      render: (text) => parseInt(text).toLocaleString(undefined, { maximumFractionDigits: 0 }),
    },
    {
      title: "Difference from Breakeven",
      dataIndex: "diff_from_be",
      key: "diff_from_be",
      align: "right",
      render: (text) => `(${formatNumberWithCommas(text)})`,
    },
    {
      title: "Difference from IE Target",
      dataIndex: "diff_from_ie_target",
      key: "diff_from_ie_target",
      align: "right",
      render: (text) => `(${formatNumberWithCommas(text)})`,
    },
    {
      title: "% of Achievement from IE target",
      dataIndex: "achievement_from_ie_target",
      key: "achievement_from_ie_target",
      align: "right",
      render: (text) => `${formatNumberWithCommas(text)}%`,
    },
    {
      title: "% of Achievement from B/E req qty",
      dataIndex: "achivement_from_be_req_qty",
      key: "achivement_from_be_req_qty",
      align: "right",
      render: (text) => `${formatNumberWithCommas(text)}%`,
    },
    {
      title: "Ranking on Achievement from B/E",
      dataIndex: "rank",
      key: "rank",
      align: "right",
    },
  ];

  return (
    <Card
      title={`
        Line wise achivement & Target details as on ${selectedDate.format("DD-MMM-YYYY")}
      `}
      bordered={false}
    >
      <Spin spinning={data.loading} tip="Loading data...">
        <br />
        <Row gutter={24}>
          <Col span={6}>
            <DatePicker
              allowClear={false}
              format={"DD-MMM-YYYY"}
              style={{
                width: "100%",
              }}
              value={selectedDate}
              onChange={handleDateChange}
            />
          </Col>
          <Col span={6}>
            <Space>
              <Button
                size="small"
                onClick={() => {
                  const d = moment();
                  setSelectedDate(d);
                  handleSearch(d);
                }}
                disabled={data.loading}
              >
                Today
              </Button>
              <Button
                size="small"
                onClick={() => {
                  const d = moment().subtract(1, "days");
                  setSelectedDate(d);
                  handleSearch(d);
                }}
                disabled={data.loading}
              >
                Yesterday
              </Button>
              <Button type="primary" danger onClick={handleReset} disabled={data.loading}>
                Reset
              </Button>
              <Button type="primary" onClick={() => handleSearch()} loading={data.loading} disabled={data.loading}>
                Search
              </Button>
            </Space>
          </Col>
        </Row>

        {hasSearched && (
          <>
            <br />
            <ListTable
              dataSource={data.dataSource}
              columns={columns}
              pagination={false}
              loading={data.loading}
              error={fetchError}
              onRetry={() => handleSearch()}
              bordered
              size="small"
              locale={{
                emptyText: `No records for ${selectedDate.format("DD-MMM-YYYY")}. Change the date above.`,
              }}
              summary={(pageData) => {
                if (pageData.length === 0) {
                  return null;
                }
                let totalRequiredQuantityForBEven = 0;
                let totalIeGivenTarget = 0;
                let totalIeTargetPercentageOfBEQty = 0;
                let totalActualProduction = 0;
                let totalDifferenceFromBreakeven = 0;
                let totalDifferenceFromIETarget = 0;
                let totalPercentageOfAchievementFromIETarget = 0;
                let totalPercentageOfAchievementFromBEQty = 0;

                pageData.forEach(
                  ({
                    require_qty,
                    ie_given_target,
                    ie_target_be_qty,
                    actual_production,
                    diff_from_be,
                    diff_from_ie_target,
                    achievement_from_ie_target,
                    achivement_from_be_req_qty,
                  }) => {
                    totalRequiredQuantityForBEven += require_qty;
                    totalIeGivenTarget += ie_given_target;
                    totalIeTargetPercentageOfBEQty += ie_target_be_qty;
                    totalActualProduction += actual_production;
                    totalDifferenceFromBreakeven += diff_from_be;
                    totalDifferenceFromIETarget += diff_from_ie_target;
                    totalPercentageOfAchievementFromIETarget += achievement_from_ie_target;
                    totalPercentageOfAchievementFromBEQty += achivement_from_be_req_qty;
                  },
                );

                return (
                  <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={3}>Total</Table.Summary.Cell>

                      <Table.Summary.Cell align="right">
                        <b>{formatNumberWithCommas(totalRequiredQuantityForBEven)}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        <b>{formatNumberWithCommas(totalIeGivenTarget)}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        <b>{formatNumberWithCommas(totalIeTargetPercentageOfBEQty)}%</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        <b>{formatNumberWithCommas(totalActualProduction)}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        <b>{formatNumberWithCommas(totalDifferenceFromBreakeven)}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        <b>{formatNumberWithCommas(totalDifferenceFromIETarget)}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        <b>{(totalPercentageOfAchievementFromIETarget / pageData.length).toFixed(2)}%</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        <b>{(totalPercentageOfAchievementFromBEQty / pageData.length).toFixed(2)}%</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>
                );
              }}
            />
            <div
              style={{
                padding: "20px",
                backgroundColor: "#ffffff",
                borderRadius: "10px",
              }}
            >
              <h3
                style={{
                  color: "#00bfff",
                  textAlign: "center",
                  marginBottom: "20px",
                }}
              >
                Today's Est. Net Profit/taka
              </h3>
              <Line {...config} />
            </div>
          </>
        )}
      </Spin>
    </Card>
  );
}
